import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import ActionButtons, { EditButton, DeleteButton } from '../../components/ActionButtons';
import { apiRequest } from '../../services/api';
import UsageInfo from '../../components/UsageInfo';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';
import ModalShell from '../../components/ModalShell';

const TIP_IKONLAR = {
  'Genel': '👤', 'Ön büro': '🏨', 'Housekeeping': '🧹',
  'Muhasebe': '💼', 'Yönetici': '👑', 'Güvenlik': '🔒', 'Restorant': '🍽️',
};
const getIkon = ad => {
  const anahtar = Object.keys(TIP_IKONLAR).find(k => ad?.toLocaleLowerCase('tr-TR').includes(k.toLocaleLowerCase('tr-TR')));
  return anahtar ? TIP_IKONLAR[anahtar] : '🏷️';
};

function FormHata({ mesaj }) {
  if (!mesaj) return null;
  return <p className="text-[10px] font-black text-rose-500 uppercase tracking-widest mt-2 flex items-center gap-1 animate-shake">⚠️ {mesaj}</p>;
}

function PersonelTipModal({ kayit, liste, onKapat, onKaydet, kaydediliyor, hata }) {
  const yeni = !kayit;
  const [form, setForm] = useState({
    ad: kayit?.ad ?? '',
    siraNo: kayit?.siraNo ?? (liste.length + 1),
  });
  const [hatalar, setHatalar] = useState({});

  const INP = (h) => `w-full border-2 rounded-2xl px-5 py-3.5 text-sm font-bold text-gray-800 transition-all bg-white focus:outline-none ${h ? 'border-rose-400 bg-rose-50 focus:border-rose-500 shadow-rose-50 shadow-inner' : 'border-gray-100 focus:border-purple-400 focus:ring-4 focus:ring-purple-50 shadow-sm'}`;

  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setHatalar(p => ({ ...p, [k]: null })); };

  const handleKaydet = () => {
    if (!form.ad.trim()) { setHatalar({ ad: 'Tip adı zorunludur.' }); return; }
    onKaydet({ ...(kayit ? { personelTipiId: kayit.personelTipiId } : {}), ...form, ad: form.ad.trim(), aktifMi: true });
  };

  return (
    <ModalShell
      icon="🏷️"
      iconColor="from-purple-600 to-pink-700"
      title={yeni ? 'Yeni Personel Tipi' : 'Tipi Düzenle'}
      subtitle="Departman ve pozisyon tanımları"
      onClose={onKapat}
      size="sm"
      footer={
        <>
          <button onClick={onKapat} className="px-8 py-3.5 rounded-2xl border-2 border-gray-100 text-xs font-black text-gray-400 hover:bg-gray-100 transition-all uppercase tracking-widest active:scale-95 leading-none">İPTAL</button>
          <button onClick={handleKaydet} disabled={kaydediliyor}
            className="px-10 py-3.5 bg-gradient-to-r from-purple-600 to-pink-700 hover:from-purple-700 hover:to-pink-800 disabled:opacity-60 text-white rounded-2xl text-xs font-black shadow-xl shadow-purple-200 transition-all hover:-translate-y-1 active:translate-y-0 tracking-widest uppercase leading-none">
            {kaydediliyor ? '⌛ İŞLEM...' : '💾 TİPİ KAYDET'}
          </button>
        </>
      }
    >
      {hata && <div className="bg-rose-50 border-2 border-rose-100 rounded-2xl px-5 py-4 text-xs text-rose-600 font-black uppercase tracking-widest leading-none animate-shake">⚠️ {hata}</div>}

      <div className="space-y-6 leading-none text-left">
        <div>
          <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">TİP / DEPARTMAN ADI *</label>
          <input value={form.ad} onChange={e => set('ad', e.target.value)} className={INP(hatalar.ad)} placeholder="ÖRN: ÖN BÜRO, HOUSEKEEPING..." autoFocus />
          <FormHata mesaj={hatalar.ad} />
        </div>

        <div>
          <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">GÖSTERİM SIRASI</label>
          <input type="number" value={form.siraNo} onChange={e => set('siraNo', parseInt(e.target.value) || 0)} className={INP(false) + ' text-center text-lg'} />
        </div>

        {form.ad.trim() && (
          <div className="mt-8 pt-8 border-t border-gray-50 flex flex-col items-center gap-5 animate-slide-up leading-none">
            <p className="text-[9px] font-black text-gray-300 uppercase tracking-widest leading-none">GÖRSEL ÖNİZLEME</p>
            <div className="bg-white p-6 rounded-[2rem] border-2 border-purple-50 shadow-xl shadow-purple-100/50 flex items-center gap-5 w-full transform-gpu ring-8 ring-purple-50/30">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-3xl shadow-xl shadow-purple-200 border-2 border-white ring-4 ring-purple-50">{getIkon(form.ad)}</div>
              <div className="leading-none text-left">
                <h4 className="font-black text-gray-800 text-lg uppercase tracking-tight leading-none mb-2">{form.ad}</h4>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none">PERSONEL TİPİ · SIRA: {form.siraNo}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </ModalShell>
  );
}

export default function PersonelTipleri() {
  const [liste, setListe] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [view, setView] = useState('liste');
  const [secili, setSecili] = useState(null);
  const [silOnay, setSilOnay] = useState(null);
  const [aktifUyari, setAktifUyari] = useState(null);
  const [arama, setArama] = useState('');
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [hata, setHata] = useState(null);

  const veriYukle = useCallback(async () => {
    setYukleniyor(true);
    try { setListe((await apiRequest('personeltipleri')) || []); } catch { /* sessiz */ }
    setYukleniyor(false);
  }, []);

  useEffect(() => { veriYukle(); }, [veriYukle]);

  async function handleKaydet(data) {
    setKaydediliyor(true); setHata(null);
    try {
      if (data.personelTipiId) await apiRequest('personeltipleri', 'PUT', data);
      else await apiRequest('personeltipleri', 'POST', data);
      await veriYukle(); setView('liste'); setSecili(null);
    } catch { setHata('Hata oluştu.'); }
    setKaydediliyor(false);
  }

  async function handleSil(id) { await apiRequest(`personeltipleri/${id}`, 'DELETE'); setListe(p => p.filter(x => x.personelTipiId !== id)); setSilOnay(null); }

  async function toggleAktif(item, onaylandi = false) {
    if (item.aktifMi && !onaylandi) {
      try {
        const res = await apiRequest(`personeltipleri/${item.personelTipiId}/personel-sayisi`);
        setAktifUyari({ item, personelSayisi: res.personelSayisi ?? 0 });
      } catch { setAktifUyari({ item, personelSayisi: 0 }); }
      return;
    }
    try {
      await apiRequest(`personeltipleri/${item.personelTipiId}/aktif?aktif=${!item.aktifMi}`, 'PATCH');
      setListe(p => p.map(x => x.personelTipiId === item.personelTipiId ? { ...x, aktifMi: !item.aktifMi } : x));
    } catch { alert('Başarısız.'); }
    setAktifUyari(null);
  }

  const filtrelenmis = useMemo(() => liste.filter(x => x.ad?.toLocaleLowerCase('tr-TR').includes(arama.toLocaleLowerCase('tr-TR'))), [liste, arama]);
  const isOpen = view === 'ekle' || view === 'duzenle';

  return (
    <div className="p-3 md:p-6 space-y-7 min-h-screen bg-gray-50/50 animate-fadeIn text-left leading-none font-sans" style={{ transform: 'translateZ(0)' }}>
      <PageHeader
        icon="🏷️"
        iconColor="from-purple-600 to-pink-700"
        iconShadow="shadow-purple-200"
        title="Personel Tipleri"
        subtitle="Departman ve pozisyon yapılandırması"
      >
        <button onClick={() => { setSecili(null); setView('ekle'); setHata(null); }}
          className="group flex-shrink-0 flex items-center justify-center gap-4 bg-gradient-to-r from-purple-600 to-pink-700 hover:from-purple-700 hover:to-pink-800 text-white px-10 py-4 rounded-2xl text-[10px] font-black shadow-2xl shadow-purple-100 transition-all hover:-translate-y-1 active:translate-y-0 uppercase tracking-widest relative overflow-hidden leading-none transform-gpu">
          <span className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></span>
          <span className="relative text-xl leading-none">＋</span> <span className="relative leading-none">YENİ TİP EKLE</span>
        </button>
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 leading-none transform-gpu">
        {[
          { label: 'TOPLAM DEPARTMAN', value: liste.length, icon: '📂', color: 'from-gray-700 to-gray-900 shadow-gray-200' },
          { label: 'AKTİF TÜRLER', value: liste.filter(x => x.aktifMi).length, icon: '✅', color: 'from-purple-500 to-purple-700 shadow-purple-100' },
          { label: 'PASİF / ARŞİV', value: liste.filter(x => !x.aktifMi).length, icon: '📦', color: 'from-pink-500 to-pink-700 shadow-pink-100' },
          { label: 'EN ÇOK KADRO', value: 'ÖNBÜRO', icon: '🏆', color: 'from-amber-400 to-orange-500 shadow-amber-100' },
        ].map(k => (
          <div key={k.label} className={`relative overflow-hidden bg-gradient-to-br ${k.color} rounded-[2rem] p-6 text-white shadow-xl hover:shadow-2xl transition-all duration-300 group leading-none`}>
            <div className="absolute -right-4 -bottom-4 text-7xl opacity-10 pointer-events-none group-hover:scale-125 transition-transform duration-500 transform-gpu leading-none">{k.icon}</div>
            <p className="text-[10px] font-black uppercase tracking-widest opacity-70 mb-1 leading-none text-left">{k.label}</p>
            <p className="text-xl md:text-2xl font-black tracking-tight leading-none text-left">{k.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-xl overflow-hidden min-h-[400px] leading-none transform-gpu transition-shadow duration-500 hover:shadow-2xl hover:shadow-purple-100/30">
        <div className="px-6 md:px-10 py-6 border-b-2 border-gray-100 bg-gray-50/20 flex flex-col md:flex-row md:items-center gap-6 leading-none">
          <div className="relative flex-1 w-full text-left leading-none">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-black leading-none">🔍</span>
            <input value={arama} onChange={e => setArama(e.target.value)} placeholder="DEPARTMAN VEYA TİP ADINA GÖRE ARA..."
              className="w-full pl-11 pr-4 py-3.5 text-sm border-2 border-gray-100 rounded-2xl focus:border-purple-400 focus:outline-none bg-white transition-all shadow-inner font-bold leading-none shadow-purple-50" />
          </div>
          <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none whitespace-nowrap ml-auto">SİSTEMDE TOPLAM <b className="text-purple-600 text-xs">{liste.length}</b> KAYIT MEVCUT</p>
        </div>

        {yukleniyor ? (
          <LoadingSpinner fullPage text="Veriler Senkronize Ediliyor..." />
        ) : (
          <>
            {/* Mobile Cards */}
            <div className="md:hidden divide-y divide-gray-50 leading-none">
              {filtrelenmis.length === 0 ? (
                <EmptyState icon="🏷️" text="Aradığınız kriterlerde sonuç bulunamadı" />
              ) : filtrelenmis.map(item => (
                <div key={item.personelTipiId} className={`p-6 flex flex-col gap-6 hover:bg-purple-50/20 transition-all transform-gpu group ${!item.aktifMi && 'grayscale-[0.5] opacity-60 bg-gray-50'}`}>
                  <div className="flex items-center justify-between gap-4 leading-none text-left">
                    <div className="flex items-center gap-5 leading-none text-left">
                      <div className="w-14 h-14 bg-white rounded-2xl shadow-xl shadow-purple-50 border border-purple-50 flex items-center justify-center text-3xl transform-gpu group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 leading-none">{getIkon(item.ad)}</div>
                      <div className="leading-none text-left">
                        <h3 className="font-black text-gray-800 text-lg uppercase tracking-tight leading-none mb-2">{item.ad}</h3>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-none">GÖSTERİM SIRASI: {item.siraNo}</p>
                      </div>
                    </div>
                    <button onClick={() => toggleAktif(item)} className={`px-4 py-2 text-[9px] font-black rounded-full border-2 transition-all duration-300 ${item.aktifMi ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-red-50 text-red-600 border-red-100'}`}>{item.aktifMi ? 'AKTİF' : 'PASİF'}</button>
                  </div>
                  <div className="flex gap-2 leading-none">
                    <EditButton size="lg" onClick={() => { setSecili(item); setView('duzenle'); setHata(null); }} />
                    <DeleteButton size="lg" onClick={() => setSilOnay(item.personelTipiId)} />
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto leading-none">
              <table className="w-full text-sm leading-none">
                <thead>
                  <tr className="bg-gray-50/50 text-[11px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">
                    <th className="py-6 px-8 text-left w-20 leading-none">#</th>
                    <th className="py-6 px-8 text-left leading-none uppercase">Departman / Tip Adı</th>
                    <th className="py-6 px-8 text-center w-32 leading-none uppercase">Sıra No</th>
                    <th className="py-6 px-8 text-center w-40 leading-none uppercase">Durum</th>
                    <th className="py-6 px-8 text-right leading-none uppercase">İşlemler</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 leading-none">
                  {filtrelenmis.length === 0 ? (
                    <EmptyState as="td" colSpan={5} icon="🏷️" text="Kayıt Bulunamadı" />
                  ) : filtrelenmis.map((item, i) => (
                    <tr key={item.personelTipiId} className={`group transition-all duration-300 hover:bg-purple-50/20 transform-gpu ${!item.aktifMi ? 'grayscale-[0.8] opacity-60 bg-gray-50/40' : ''}`}>
                      <td className="py-6 px-8 font-black text-gray-300 text-xs leading-none">{(i + 1).toString().padStart(2, '0')}</td>
                      <td className="py-6 px-8 text-left leading-none font-black text-gray-700 text-base uppercase tracking-tight">
                        <div className="flex items-center gap-4 leading-none text-left">
                          <div className="w-11 h-11 bg-white rounded-xl shadow-lg shadow-purple-50 border border-purple-50 flex items-center justify-center text-2xl group-hover:scale-110 transition-transform leading-none">{getIkon(item.ad)}</div>
                          <span className="group-hover:text-purple-600 transition-colors uppercase leading-none">{item.ad}</span>
                        </div>
                      </td>
                      <td className="py-6 px-8 text-center leading-none">
                        <span className="text-[10px] font-black text-gray-400 bg-gray-100 px-3 py-1.5 rounded-lg leading-none uppercase tracking-widest">{item.siraNo}</span>
                      </td>
                      <td className="py-6 px-8 text-center leading-none">
                        <button onClick={() => toggleAktif(item)} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-2xl border-2 transition-all duration-300 uppercase text-[10px] font-black tracking-widest shadow-sm ${item.aktifMi ? 'bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-red-50 hover:text-red-600 hover:border-red-100' : 'bg-red-50 text-red-600 border-red-100 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-100'}`}>
                          {item.aktifMi ? '✓ AKTİF' : '✗ PASİF'}
                        </button>
                      </td>
                      <td className="py-6 px-8 text-right leading-none">
                        <div className="flex items-center justify-end gap-3 leading-none">
                          <ActionButtons onEdit={() => { setSecili(item); setView('duzenle'); setHata(null); }} onDelete={() => setSilOnay(item.personelTipiId)} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <UsageInfo
        color="purple"
        description="Personel Tipleri, otelinizdeki çalışanların departmanlarını veya görev türlerini (Örn: Ön Büro, Kat Hizmetleri) tanımlayan listedir."
        scenarios={[
          { title: "Departman Organizasyonu", content: "Yeni bir personel kaydı oluştururken departman seçimi bu listeden yapılır, böylece personelleriniz birim bazlı gruplanır." },
          { title: "Sıralama ve Öncelik", content: "Listelerdeki gösterim sırasını değiştirerek, operasyonda en çok kullanılan departmanları personel kartlarında en üstte getirebilirsiniz." }
        ]}
      />

      {isOpen && createPortal(
        <PersonelTipModal kayit={secili} liste={liste} onKapat={() => setView('liste')} onKaydet={handleKaydet} kaydediliyor={kaydediliyor} hata={hata} />,
        document.body
      )}

      {aktifUyari && createPortal(
        <div className="fixed inset-0 bg-black/60 z-[110] flex items-center justify-center p-4 backdrop-blur-md animate-fade-in shadow-2xl leading-none font-black uppercase text-center">
          <div className="bg-white rounded-[2.5rem] shadow-2xl p-10 w-full max-w-sm border border-gray-100 leading-none">
            <div className="w-24 h-24 bg-amber-50 rounded-[2.5rem] flex items-center justify-center mx-auto mb-6 text-5xl shadow-xl shadow-amber-100/50 animate-bounce-subtle leading-none">⚠️</div>
            <h3 className="font-black text-gray-900 text-xl tracking-tight leading-none uppercase">Departmanı Kapat</h3>
            <p className="text-[11px] font-black text-gray-400 mt-5 mb-8 leading-relaxed uppercase tracking-tight leading-loose italic">
              <b className="text-gray-900">"{aktifUyari.item.ad}"</b> departmanı pasife alınacak. Emin misiniz?
            </p>
            {aktifUyari.personelSayisi > 0 && (
              <div className="bg-amber-50 p-4 rounded-2xl border border-amber-100 mb-8 leading-none text-left">
                <p className="text-[10px] font-black text-amber-700 uppercase tracking-widest leading-none mb-1">DİKKAT: BAĞLI PERSONEL VAR</p>
                <p className="text-[11px] font-bold text-amber-800 leading-tight">BU TİPE BAĞLI <b className="text-lg">{aktifUyari.personelSayisi}</b> PERSONEL BULUNUYOR. PASİFE ALMA İŞLEMİ MEVCUT PERSONELLERİ ETKİLEMEZ ANCAK YENİ KAYITLARDA BU TİP SEÇİLEMEZ.</p>
              </div>
            )}
            <div className="flex gap-4 leading-none">
              <button onClick={() => setAktifUyari(null)} className="flex-1 py-4 bg-gray-50 text-gray-600 rounded-2xl text-[10px] font-black hover:bg-gray-100 transition-all uppercase leading-none">İPTAL</button>
              <button onClick={() => toggleAktif(aktifUyari.item, true)} className="flex-1 py-4 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-2xl text-[10px] font-black shadow-xl shadow-amber-100 transition-all uppercase leading-none">EVET, KAPAT</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {silOnay && createPortal(
        <div className="fixed inset-0 bg-black/60 z-[110] flex items-center justify-center p-4 backdrop-blur-md animate-fade-in shadow-2xl leading-none font-black uppercase text-center">
          <div className="bg-white rounded-[2.5rem] shadow-2xl p-10 w-full max-w-sm border border-gray-100 leading-none">
            <div className="w-24 h-24 bg-rose-50 rounded-[2.5rem] flex items-center justify-center mx-auto mb-6 text-5xl shadow-xl shadow-rose-100/50 animate-bounce-subtle leading-none">🗑️</div>
            <h3 className="font-black text-gray-900 text-xl tracking-tight leading-none uppercase">Kaydı Sil</h3>
            <p className="text-[11px] font-black text-gray-400 mt-5 mb-10 leading-relaxed uppercase tracking-tight leading-loose italic">BU PERSONEL TİPİ KAYDI KALICI OLARAK SİLİNECEKTİR. EMİN MİSİNİZ?</p>
            <div className="flex gap-4">
              <button onClick={() => setSilOnay(null)} className="flex-1 py-4 bg-gray-50 text-gray-600 rounded-2xl text-[10px] font-black hover:bg-gray-100 transition-all uppercase tracking-widest active:scale-95 leading-none">İPTAL</button>
              <button onClick={() => handleSil(silOnay)} className="flex-1 py-4 bg-gradient-to-r from-rose-500 to-red-600 text-white rounded-2xl text-[10px] font-black shadow-xl shadow-rose-100 transition-all uppercase tracking-widest transform-gpu active:scale-95 leading-none font-black text-lg">EVET, SİL</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        .animate-scale-in { animation: scaleIn 0.3s ease-out forwards; }
        @keyframes shake { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-5px); } 75% { transform: translateX(5px); } }
        .animate-shake { animation: shake 0.3s ease-in-out infinite; animation-iteration-count: 2; }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-slide-up { animation: slideUp 0.5s ease-out forwards; }
        @keyframes bounceSubtle { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        .animate-bounce-subtle { animation: bounceSubtle 2s infinite; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}